import React, { Component, createRef } from 'react';
import AcknowledgementModal from '../components/AcknowledgementModal';
import Layout from '../components/Layout';
import '../styles/custom.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TextField, TablePagination, Box, Chip, Button, CircularProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import apiService from '../services/apiService';
import { MpnProviderSearchCriteria, MpnProvider, MpnProviderSearchResponse } from '@wcp-ca-mpn/shared';

interface Provider {
    id: number;
    providerName: string;
    degreeList: string;
    groupAffiliation: string;
    address: string;
    phone: string;
    tin: string;
    provId: string;
    acknowledgement: boolean;
    mpnParticipation: { name: string; ids: string[] }[];
}

interface FilterState {
    providerName: string;
    groupAffiliation: string;
    address: string;
    phone: string;
    tin: string;
    [key: string]: string;
}

interface ErrorState {
    form?: string;
    firstName?: string;
    lastName?: string;
    taxId?: string;
    officePhone?: string;
    search?: string;
    [key: string]: string | undefined;
}

interface SortState {
    column: string;
    direction: 'asc' | 'desc';
}

interface ProviderSearchState {
    search: MpnProviderSearchCriteria;
    results: Provider[];
    filters: FilterState;
    hasSearched: boolean;
    errors: ErrorState;
    page: number;
    rowsPerPage: number;
    loading: boolean;
    showAcknowledgementModal: boolean;
    modalProvider: Provider | null;
    selectedProviderId?: number;
    sort: SortState;
}

const columns = [
    { id: 'providerName', label: 'Provider Name', filterable: true },
    { id: 'groupAffiliation', label: 'Group Affiliation', filterable: true },
    { id: 'address', label: 'Address', filterable: true },
    { id: 'phone', label: 'Office Phone', filterable: true },
    { id: 'tin', label: 'Tax ID', filterable: true },
    { id: 'acknowledgement', label: 'Acknowledgement', filterable: false }
];

const initialSearch: MpnProviderSearchCriteria = {
    firstName: '',
    lastName: '',
    groupAffiliation: '',
    taxId: '',
    officePhone: '',
    state: 'CA'
};

function mapApiProviderToUiProvider(apiProvider: MpnProvider, index: number): Provider {
    //  full address from response fields
    // Format city as title case, state as uppercase
    function toTitleCase(str: string) {
        return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
    const city = apiProvider.addressCity ? toTitleCase(apiProvider.addressCity) : '';
    const state = apiProvider.addressState ? apiProvider.addressState.toUpperCase() : '';
    const zip = apiProvider.addressZip ? apiProvider.addressZip.trim() : '';
    let address = apiProvider.addressStreet1 ? apiProvider.addressStreet1.trim() : '';
    if (apiProvider.addressStreet2 && apiProvider.addressStreet2.trim().length > 0) {
        address += ', ' + apiProvider.addressStreet2.trim() + '\n';
    } else if (address) {
        address += '\n';
    }
    let cityStateZip = '';
    if (city) {
        cityStateZip += city;
    }
    if (state) {
        cityStateZip += (city ? ', ' : '') + state;
    }
    if (zip) {
        cityStateZip += (state ? ' ' : ' ') + zip;
    }
    const fullAddress = (address + cityStateZip).trim();
    // Format as LastName, FirstName MiddleInitial
    let middleInitial = '';
    if (apiProvider.middleName && apiProvider.middleName.trim().length > 0) {
        middleInitial = apiProvider.middleName.trim().charAt(0).toUpperCase() + '.';
    }
    const degreeList = Array.isArray(apiProvider.degrees) && apiProvider.degrees.length > 0 ? apiProvider.degrees.join(', ') : '';
    // Build displayName: LastName, FirstName[, MiddleInitial.], Degree1, Degree2
    let displayName = `${apiProvider.lastName}, ${apiProvider.firstName}`;
    if (middleInitial) {
        displayName += ` ${middleInitial},`;
    } else {
        displayName += ',';
    }
    if (degreeList) {
        displayName += ` ${degreeList}`;
    }
    displayName = displayName.trim();
    return {
        id: index + 1,
        providerName: displayName,
        degreeList,
        groupAffiliation: apiProvider.officeName || '',
        address: fullAddress,
        phone: apiProvider.phoneNum || '',
        tin: apiProvider.taxId || '',
        provId: apiProvider.providerId || '',
        acknowledgement: Array.isArray(apiProvider.mpns) && apiProvider.mpns.length > 0,
        mpnParticipation: Array.isArray(apiProvider.mpns)
            ? apiProvider.mpns.map(mpn => ({ name: mpn.mpnName, ids: mpn.dwcNumbers }))
            : []
    };
}

interface SortState {
    column: string;
    direction: 'asc' | 'desc';
}

class ProviderSearch extends Component<{}, ProviderSearchState> {
    state: ProviderSearchState = {
        search: initialSearch,
        results: [],
        filters: {
            providerName: '',
            groupAffiliation: '',
            address: '',
            phone: '',
            tin: ''
        },
        hasSearched: false,
        errors: {},
        page: 0,
        rowsPerPage: 10,
        loading: false,
        showAcknowledgementModal: false,
        modalProvider: null,
        selectedProviderId: undefined,
        sort: { column: 'providerName', direction: 'asc' }
    };

    handleSort = (column: string) => {
        if (column === 'acknowledgement') return;
        this.setState(prev => {
            const { sort } = prev;
            if (sort.column === column) {
                // Toggle direction
                return { sort: { column, direction: sort.direction === 'asc' ? 'desc' : 'asc' } };
            } else {
                // New column, default to ascending
                return { sort: { column, direction: 'asc' } };
            }
        });
    };
    firstNameRef: React.RefObject<HTMLInputElement | null>;

    constructor(props: {}) {
        super(props);
        this.firstNameRef = createRef<HTMLInputElement>();
    }

    handleShowAcknowledgement = (provider: Provider) => {
        this.setState({
            showAcknowledgementModal: true,
            modalProvider: provider
        });
    };

    handleCloseAcknowledgementModal = () => {
        this.setState({
            showAcknowledgementModal: false,
            modalProvider: null
        });
    };

 handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        this.setState(prev => {
            const search = { ...prev.search };
            const errors = { ...prev.errors };
            
            if (name === "officePhone") {
                const digits = value.replace(/\D/g, '').slice(0, 10);
                (search as any)[name] = digits;
                (errors as any)[name] = undefined;
            } else {
                (search as any)[name] = value;
                (errors as any)[name] = undefined;
            }

            // Clear the form error if we now have valid search criteria
            const firstName = (search.firstName ?? '').trim();
            const lastName = (search.lastName ?? '').trim();
            const groupAffiliation = (search.groupAffiliation ?? '').trim();
            const taxId = (search.taxId ?? '').replace(/\D/g, '');
            const officePhone = (search.officePhone ?? '').replace(/\D/g, '');

            // Check if we now have at least one valid search criterion
            const hasName = firstName && lastName;
            if (hasName || groupAffiliation || taxId || officePhone) {
                errors.form = undefined; // Clear the form error
            }

            return { search, errors };
        });
    };
        validate = (): boolean => {
        const { search } = this.state;
        const newErrors: ErrorState = {};
        
        // Sanitize all fields
        const firstName = (search.firstName ?? '').trim();
        const lastName = (search.lastName ?? '').trim();
        const groupAffiliation = (search.groupAffiliation ?? '').trim();
        let taxId = (search.taxId ?? '').replace(/\D/g, '');
        let officePhone = (search.officePhone ?? '').replace(/\D/g, '');

        // At least one of the main search criteria must be met
        const hasName = firstName && lastName;
        if (!hasName && !groupAffiliation && !taxId && !officePhone) {
            newErrors.form = 'Please enter either both Provider First and Last Name, or Group Affiliation, or Tax ID, or Office Phone.';
        }

        // Provider Name validation (if present)
        const nameRegex = /^[A-Za-z\s\-'.,()]*$/; // /^[A-Za-z\s-]*$/;
        let nameErrors = [];

        // If first name is provided, last name must also be provided (min 2 chars)
        if (firstName && (!lastName || lastName.length < 2)) {
            nameErrors.push('Last name is required when first name is provided');
            newErrors.lastName = 'Last name is required when first name is provided';
        }

        if (firstName) {
            if (!nameRegex.test(firstName)) {
                nameErrors.push('First name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.');
                newErrors.firstName = 'First name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.';
            } else if (firstName.length < 2) {
                nameErrors.push('First name must be at least 2 characters.');
                newErrors.firstName = 'First name must be at least 2 characters.';
            }
        }
        if (lastName) {
            if (!nameRegex.test(lastName)) {
                nameErrors.push('Last name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.');
                newErrors.lastName = 'Last name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.';
            } else if (lastName.length < 2) {
                nameErrors.push('Last name must be at least 2 characters.');
                newErrors.lastName = 'Last name must be at least 2 characters.';
            }
        }

        // Show illegal character and min length validation for both fields independently
        if (firstName) {
            if (!nameRegex.test(firstName)) {
                newErrors.firstName = 'First name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.';
            } else if (firstName.length < 2) {
                newErrors.firstName = 'First name must be at least 2 characters.';
            }
        }
        if (lastName) {
            if (!nameRegex.test(lastName)) {
                newErrors.lastName = 'Last name can contain only letters, spaces, hyphens, apostrophes, periods, commas, or parentheses.';
            } else if (lastName.length < 2) {
                newErrors.lastName = 'Last name must be at least 2 characters.';
            }
        }

        // Rest of validation...
        if (groupAffiliation && groupAffiliation.length < 3) {
            newErrors.groupAffiliation = 'Group affiliation must be at least 3 characters.';
        }

        if (search.taxId && taxId.length !== 9) {
            newErrors.taxId = 'Invalid Tax ID. Please enter a valid Tax ID number.';
        }

        if (search.officePhone && officePhone.length !== 10) {
            newErrors.officePhone = 'Phone number must be exactly 10 digits.';
        }

        this.setState({ errors: newErrors });
        return Object.keys(newErrors).length === 0;
    };
       handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!this.validate()) {
            this.setState({ results: [] });
            return;
        }

        const { search, errors } = this.state;
        // Build a fully-typed payload for the API
        const searchPayload: MpnProviderSearchCriteria = {
            firstName: search.firstName && search.firstName.trim().length >= 2 ? search.firstName.trim() : '',
            lastName: search.lastName && search.lastName.trim().length >= 2 ? search.lastName.trim() : '',
            groupAffiliation: search.groupAffiliation && search.groupAffiliation.trim().length >= 3 ? search.groupAffiliation.trim() : '',
            taxId: search.taxId && search.taxId.replace(/\D/g, '').length === 9 ? search.taxId.replace(/\D/g, '') : '',
            officePhone: search.officePhone && search.officePhone.replace(/\D/g, '').length === 10 ? search.officePhone.replace(/\D/g, '') : '',
            state: 'CA'
        };
        // Defensive: only call API if at least one valid search criterion is present
        const hasName = searchPayload.firstName && searchPayload.lastName;
        if (!hasName && !searchPayload.groupAffiliation && !searchPayload.taxId && !searchPayload.officePhone) {
            return;
        }

        try {
            this.setState({ results: [], hasSearched: true, loading: true, selectedProviderId: undefined });
            const apiResponse: MpnProviderSearchResponse = await apiService.searchProviders(searchPayload);
            const apiProviders = apiResponse?.data?.providers || [];
            const providerResults = Array.isArray(apiProviders)
                ? apiProviders.map(mapApiProviderToUiProvider)
                : [];
            this.setState({ 
                results: providerResults, 
                page: 0, 
                rowsPerPage: 10,
                filters: {
                    providerName: '',
                    groupAffiliation: '',
                    address: '',
                    phone: '',
                    tin: ''
                }, 
                loading: false,
                selectedProviderId: undefined
            });
            if (providerResults.length === 0) {
                this.setState({ errors: { ...errors, search: "No providers match your search criteria. Try searching by using fewer or partial criteria." } });
            } else {
                this.setState(prev => ({ errors: { ...prev.errors, search: undefined } }));
            }
        } catch (error) {
            this.setState({ loading: false });
            console.error("Error searching providers:", error);
            let errorMessage = "An error occurred while searching for providers. Please try again.";
            if (error instanceof Error) {
                if (error.message.includes('400')) {
                    errorMessage = "Invalid search criteria. Please check your input and try again.";
                } else if (error.message.includes('timeout')) {
                    errorMessage = "The search request timed out. Please try again later.";
                } else if (error.message.includes('network')) {
                    errorMessage = "Network error. Please check your connection and try again.";
                }
            }
            this.setState({ errors: { ...errors, search: errorMessage }, results: [] });
        }
    };
     handleReset = () => {
        this.setState({
            search: initialSearch,
            results: [],
            hasSearched: false,
            filters: {
                providerName: '',
                groupAffiliation: '',
                address: '',
                phone: '',
                tin: ''
            },
            page: 0,
            rowsPerPage: 10,
            errors: {},
            loading: false
        }, () => {
            if (this.firstNameRef.current) {
                this.firstNameRef.current.focus();
            }
        });
    };
handleFilterChange = (
        e: React.ChangeEvent<HTMLInputElement> | { target: { value: string } },
        columnId: keyof FilterState
    ) => {
        this.setState(prev => ({
            filters: {
                ...prev.filters,
                [columnId]: (e.target as any).value
            },
            page: 0
        }));
    };

    clearAllFilters = () => {
        this.setState({
            filters: {
                providerName: '',
                groupAffiliation: '',
                address: '',
                phone: '',
                tin: ''
            },
            page: 0
        });
    };

    handleChangePage = (event: unknown, newPage: number) => {
        this.setState({ page: newPage });
    };

    handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
    };
    render() {
        const { search, results, filters, hasSearched, errors, page, rowsPerPage, loading, showAcknowledgementModal, modalProvider } = this.state;

        // Sort results by selected column/direction
        const { sort } = this.state;
        const sortedResults = [...results].sort((a, b) => {
            let valA = a[sort.column as keyof Provider];
            let valB = b[sort.column as keyof Provider];
            // For undefined/null
            if (valA == null) valA = '';
            if (valB == null) valB = '';
            // For string comparison (case-insensitive)
            if (typeof valA === 'string' && typeof valB === 'string') {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }
            if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
            return 0;
        });
        const filteredResults = sortedResults.filter(row =>
            Object.keys(filters).every(key => {
                if (filters[key as keyof FilterState] === '') return true;
                return row[key as keyof Provider] && row[key as keyof Provider].toString().toLowerCase().includes(filters[key as keyof FilterState].toLowerCase());
            })
        );
        const pagedResults = filteredResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        const activeFiltersCount = Object.values(filters).filter(v => v).length;

        return (
            <Layout
                onContactUs={() => {}}
                onClose={() => {}}
            >
                <div className="provider-search-main">
                    <div className="provider-search-card">
                        <h2 className="provider-search-heading">MPN PROVIDER SEARCH</h2>
                        <p className="provider-search-subtext">
                            Enter search criteria to find providers in our network
                        </p>
                        <form className="provider-search-form" onSubmit={this.handleSearch} noValidate>
                            {errors.form && (
                                <div className="form-error">{errors.form}</div>
                            )}
                            {/* Provider Name Row */}
                            <div className="form-row">
                                <label className="form-label">
                                    Provider Name
                                    <Tooltip title="Enter First and Last Name. Partial names are supported (minimum of 2 characters)">
                                        <InfoOutlinedIcon className="info-icon" fontSize="small" />
                                    </Tooltip>
                                </label>
                                <div className="form-inputs-inline form-inputs-vertical">
                                    <div className="form-inputs-row">
                                        <input
                                            name="firstName"
                                            value={search.firstName ?? ''}
                                            onChange={this.handleChange}
                                            className={`form-input${errors.firstName ? ' input-error-border' : ''}`}
                                            placeholder="First Name"
                                            ref={this.firstNameRef}
                                            disabled={loading}
                                        />
                                        <input
                                            name="lastName"
                                            value={search.lastName ?? ''}
                                            onChange={this.handleChange}
                                            className={`form-input${errors.lastName ? ' input-error-border' : ''}`}
                                            placeholder="Last Name"
                                            disabled={loading}
                                        />
                                    </div>
                                    {/* Error messages for names */}
                                    <div className="form-inputs-row">
                                        <div style={{ flex: 1 }}>
                                            {!errors.form && errors.firstName && (
                                                <div className="input-error-message">{errors.firstName}</div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {!errors.form && errors.lastName && (
                                                <div className="input-error-message">{errors.lastName}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Group Affiliation Row */}
                            <div className="form-row">
                                <label className="form-label">
                                    Group Affiliation
                                    <Tooltip title="Enter the provider's group affiliation. Partial name is supported (Minimum of first 3 characters).">
                                        <InfoOutlinedIcon className="info-icon" fontSize="small" />
                                    </Tooltip>
                                </label>
                                <div className="form-inputs-vertical">
                                    <input
                                        name="groupAffiliation"
                                        value={search.groupAffiliation ?? ''}
                                        onChange={this.handleChange}
                                        className={`form-input${errors.groupAffiliation ? ' input-error-border' : ''}`}
                                        placeholder="Group Affiliation"
                                        disabled={loading}
                                    />
                                    {errors.groupAffiliation && (
                                        <div className="input-error-message">{errors.groupAffiliation}</div>
                                    )}
                                </div>
                            </div>

                            {/* Tax ID Row */}
                            <div className="form-row">
                                <label className="form-label">
                                    Tax ID
                                    <Tooltip title="Enter the 9 digits Tax ID Number in this format: 123456789">
                                        <InfoOutlinedIcon className="info-icon" fontSize="small" />
                                    </Tooltip>
                                </label>
                                <div className="form-inputs-vertical">
                                    <input
                                        name="taxId"
                                        value={search.taxId ?? ''}
                                        onChange={this.handleChange}
                                        className={`form-input${errors.taxId ? ' input-error-border' : ''}`}
                                        maxLength={9}
                                        inputMode="numeric"
                                        pattern="\d*"
                                        placeholder="Tax ID"
                                        disabled={loading}
                                    />
                                    {errors.taxId && (
                                        <div className="input-error-message">{errors.taxId}</div>
                                    )}
                                </div>
                            </div>

                            {/* Office Phone Row */}
                            <div className="form-row">
                                <label className="form-label">
                                    Office Phone
                                    <Tooltip title="Enter area code and office phone number in this format: 1234445555">
                                        <InfoOutlinedIcon className="info-icon" fontSize="small" />
                                    </Tooltip>
                                </label>
                                <div className="form-inputs-vertical">
                                    <input
                                        name="officePhone"
                                        value={search.officePhone ?? ''}
                                        onChange={this.handleChange}
                                        className={`form-input${errors.officePhone ? ' input-error-border' : ''}`}
                                        maxLength={10}
                                        inputMode="numeric"
                                        pattern="\d*"
                                        placeholder="Office Phone"
                                        disabled={loading}
                                    />
                                    {errors.officePhone && (
                                        <div className="input-error-message">{errors.officePhone}</div>
                                    )}
                                </div>
                            </div>

                            {/* State Row */}
                            <div className="form-row">
                                <label className="form-label" htmlFor="state-input">State</label>
                                <input
                                    id="state-input"
                                    name="state"
                                    value={search.state ?? ''}
                                    className="form-input state-input"
                                    disabled
                                />
                            </div>

                            {/* Form Buttons */}
                            <div className="form-buttons">
                                <button type="submit" className="search-btn" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <CircularProgress size={16} color="inherit" />
                                            SEARCHING...
                                        </>
                                    ) : (
                                        <>
                                            <SearchIcon fontSize="small" className="search-btn-icon" />
                                            SEARCH
                                        </>
                                    )}
                                </button>
                                <button type="button" className="reset-btn" onClick={this.handleReset} disabled={loading}>
                                    <ClearIcon fontSize="small" className="reset-btn-icon" />
                                    RESET
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Results Section */}
                    {hasSearched && (
                        <div className="provider-results-section">
                            <div className="results-header">
                                <h3 className="provider-results-heading">
                                    {loading ? (
                                        <span>Loading results...</span>
                                    ) : (
                                        <span>MPN Provider Results ({filteredResults.length} found)</span>
                                    )}
                                </h3>
                                {errors.search && (
                                    <div className="form-error">{errors.search}</div>
                                )}
                            </div>
                            {loading && (
                                <div className="loading-container" style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '40px',
                                    background: 'white',
                                    borderRadius: '16px',
                                    boxShadow: '0 8px 32px rgba(15, 25, 125, 0.08)'
                                }}>
                                    <CircularProgress size={40} />
                                    <span style={{ marginLeft: '16px', fontSize: '16px', color: 'var(--text-secondary)' }}>
                                        Searching providers...
                                    </span>
                                </div>
                            )}
                            {!loading && results.length > 0 && (
                                <>
                                    {activeFiltersCount > 0 && (
                                        <Box className="active-filters-box">
                                            <div className="active-filters-header">
                                                <h4 className="active-filters-title">
                                                    Active Filters ({activeFiltersCount})
                                                </h4>
                                                <Button
                                                    onClick={this.clearAllFilters}
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    className="clear-filters-btn"
                                                >
                                                    Clear All Filters
                                                </Button>
                                            </div>
                                            <Box className="active-filters-chips">
                                                {Object.entries(filters).map(([key, value]) =>
                                                    value && (
                                                        <Chip
                                                            key={key}
                                                            label={`${columns.find(col => col.id === key)?.label}: ${value}`}
                                                            onDelete={() => this.handleFilterChange({ target: { value: '' } }, key as keyof FilterState)}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    )
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    <TableContainer component={Paper} className="results-table-container">
                                        <TablePagination
                                            className="provider-pagination top-pagination"
                                            rowsPerPageOptions={[5,10, 25, 50, 100,200]}
                                            component="div"
                                            count={filteredResults.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={this.handleChangePage}
                                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                                            labelRowsPerPage="Rows per page:"
                                            showFirstButton
                                            showLastButton
                                        />
                                        <Table className="provider-results-table">
                                            <TableHead>
                                                <TableRow>
                                                    {columns.map(col => (
                                                        <TableCell
                                                            key={col.id}
                                                            className="provider-table-header-cell"
                                                            onClick={() => this.handleSort(col.id)}
                                                            style={{ cursor: col.id !== 'acknowledgement' ? 'pointer' : 'default', userSelect: 'none' }}
                                                        >
                                                            {col.label}
                                                            {this.state.sort.column === col.id && col.id !== 'acknowledgement' && (
                                                                <span style={{ marginLeft: 4 }}>
                                                                    {this.state.sort.direction === 'asc' ? '▲' : '▼'}
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                                <TableRow className="table-filter-row">
                                                    {columns.map(col => (
                                                        <TableCell
                                                            key={col.id + '-filter'}
                                                            className="provider-table-filter-cell"
                                                        >
                                                            {col.filterable ? (
                                                                <TextField
                                                                    variant="outlined"
                                                                    size="small"
                                                                    placeholder={`Filter ${col.label}`}
                                                                    value={filters[col.id as keyof FilterState]}
                                                                    onChange={e => this.handleFilterChange(e, col.id as keyof FilterState)}
                                                                    fullWidth
                                                                    className="provider-table-filter-input"
                                                                />
                                                            ) : null}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {pagedResults.map((row: Provider, index: number) => (
                                                    <TableRow
                                                        key={row.id}
                                                        className={index % 2 === 0 ? "provider-table-row-even" : "provider-table-row-odd"}
                                                    >
                                                        <TableCell className="provider-table-cell provider-table-cell-bold">
                                                            {row.providerName}
                                                        </TableCell>
                                                        <TableCell className="provider-table-cell">
                                                            {row.groupAffiliation}
                                                        </TableCell>
                                                        <TableCell className="provider-table-cell provider-table-cell-address">
                                                            {row.address.split('\n').map((line, i) => (
                                                                <span key={i}>
                                                                    {line}
                                                                    {i < row.address.split('\n').length - 1 && <br />}
                                                                </span>
                                                            ))}
                                                        </TableCell>
                                                        <TableCell className="provider-table-cell provider-table-cell-phone">
                                                            {row.phone}
                                                        </TableCell>
                                                        <TableCell className="provider-table-cell provider-table-cell-taxid">
                                                            {row.tin}
                                                        </TableCell>
                                                        <TableCell className="provider-table-cell provider-table-cell-center">
                                                            {row.acknowledgement ? (
                                                                <button
                                                                    className="acknowledgement-link"
                                                                    onClick={() => this.handleShowAcknowledgement(row)}
                                                                >
                                                                    Acknowledgement Form
                                                                </button>
                                                            ) : (
                                                                <span className="provider-table-cell-na">Not Available</span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            className="provider-pagination bottom-pagination"
                                            rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                                            component="div"
                                            count={filteredResults.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={this.handleChangePage}
                                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                                            labelRowsPerPage="Rows per page:"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </TableContainer>
                                </>
                            )}
                        </div>
                    )}
                    {/* Modal */}
                    {showAcknowledgementModal && modalProvider && (
                        <AcknowledgementModal
                            provider={modalProvider}
                            onClose={this.handleCloseAcknowledgementModal}
                        />
                    )}
                </div>
            </Layout>
        );
    }
}

export default ProviderSearch;